import { useToast } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { ApiError, useApiClient } from "../../../apiClient";
import { translate } from "../../../i18n";

type CancelParams = {
    blockadeId: string;
    organizationId: string;
};

export const useCancelAdminBooking = (onSuccess: () => void) => {
    const apiClient = useApiClient();
    const toast = useToast();
    return useMutation({
        mutationKey: ['cancelAdminBooking'],
        mutationFn: ({ blockadeId, organizationId }: CancelParams) =>
            apiClient.fetchApi<{ ok: true }, {}>({
                method: 'DELETE',
                endpoint: `payments/admin-bookings/${blockadeId}?organizationId=${encodeURIComponent(organizationId)}`,
            }),
        onSuccess: () => {
            toast({
                title: 'Réservation annulée',
                description:
                    'Le lien de paiement a été expiré et le créneau libéré.',
                status: 'success',
                duration: 4000,
                position: 'top-right',
            });
            onSuccess();
        },
        onError: (err) => {
            // 409 — the player already paid. Cancelling here would leave a
            // paid Stripe session orphaned; operator needs to refund instead.
            if (err instanceof ApiError && err.status === 409) {
                toast({
                    title: 'Le joueur a déjà payé',
                    description:
                        "Rembourse via Stripe dashboard plutôt que d'annuler.",
                    status: 'warning',
                    duration: 6000,
                    position: 'top-right',
                    isClosable: true,
                });
                return;
            }
            // 404 — the blockade is already gone (cron sweep beat us, another
            // operator cancelled in a parallel tab, etc.). The intent
            // ("free this slot") is satisfied either way, so collapse this
            // back to the success path: close the modal + invalidate the
            // calendar so the slot stops showing as blocked. Without firing
            // onSuccess here the operator stares at a stale modal until they
            // close it manually.
            if (err instanceof ApiError && err.status === 404) {
                onSuccess();
                return;
            }
            // 400 — API rejected with an explanatory message worth surfacing.
            if (err instanceof ApiError && err.status === 400) {
                toast({
                    title: err.message,
                    status: 'warning',
                    duration: 4000,
                    position: 'top-right',
                });
                return;
            }
            toast({
                title: translate('error'),
                status: 'error',
                duration: 3000,
                position: 'top-right',
            });
        },
    });
};
